import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { storage } from '@/utils';

interface ProjectType {
  id: string;
  name: string;
  description?: string;
  estimatedHours?: number;
  complexity?: string;
}

interface ProjectTypeManagerProps {
  onProjectTypeAdded?: (projectType: ProjectType) => void;
  onProjectTypeUpdated?: (projectType: ProjectType) => void;
  onProjectTypeDeleted?: (projectTypeId: string) => void;
}

export const ProjectTypeManager: React.FC<ProjectTypeManagerProps> = ({
  onProjectTypeAdded,
  onProjectTypeUpdated,
  onProjectTypeDeleted,
}) => {
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newProjectType, setNewProjectType] = useState({ 
    name: '', 
    description: '', 
    estimatedHours: 0,
    complexity: 'intermediate'
  });
  const [editProjectType, setEditProjectType] = useState({ 
    name: '', 
    description: '', 
    estimatedHours: 0,
    complexity: 'intermediate'
  });

  // Load project types
  useEffect(() => {
    loadProjectTypes();
  }, []);

  const loadProjectTypes = async () => {
    try {
      const token = storage.getToken();
      if (!token) {
        console.error('No authentication token found');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/v1/templates/user/project-types', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setProjectTypes(data);
      }
    } catch (error) {
      console.error('Error loading project types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newProjectType.name.trim()) return;

    const token = storage.getToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch('/api/v1/templates/user/project-types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newProjectType.name.trim(),
          description: newProjectType.description.trim() || undefined,
          estimatedHours: newProjectType.estimatedHours || 0,
          complexity: newProjectType.complexity,
        }),
      });

      if (response.ok) {
        const createdProjectType = await response.json();
        setProjectTypes([...projectTypes, createdProjectType]);
        setNewProjectType({ name: '', description: '', estimatedHours: 0, complexity: 'intermediate' });
        setIsAdding(false);
        onProjectTypeAdded?.(createdProjectType);
      }
    } catch (error) {
      console.error('Error creating project type:', error);
    }
  };

  const handleEdit = async (id: string) => {
    if (!editProjectType.name.trim()) return;

    const token = storage.getToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch(`/api/v1/templates/user/project-types/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editProjectType.name.trim(),
          description: editProjectType.description.trim() || undefined,
          estimatedHours: editProjectType.estimatedHours || 0,
          complexity: editProjectType.complexity,
        }),
      });

      if (response.ok) {
        const updatedProjectType = await response.json();
        setProjectTypes(projectTypes.map(pt => pt.id === id ? updatedProjectType : pt));
        setEditingId(null);
        setEditProjectType({ name: '', description: '', estimatedHours: 0, complexity: 'intermediate' });
        onProjectTypeUpdated?.(updatedProjectType);
      }
    } catch (error) {
      console.error('Error updating project type:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this project type?')) return;

    const token = storage.getToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch(`/api/v1/templates/user/project-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setProjectTypes(projectTypes.filter(pt => pt.id !== id));
        onProjectTypeDeleted?.(id);
      }
    } catch (error) {
      console.error('Error deleting project type:', error);
    }
  };

  const startEdit = (projectType: ProjectType) => {
    setEditingId(projectType.id);
    setEditProjectType({ 
      name: projectType.name, 
      description: projectType.description || '',
      estimatedHours: projectType.estimatedHours || 0,
      complexity: projectType.complexity || 'intermediate'
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditProjectType({ name: '', description: '', estimatedHours: 0, complexity: 'intermediate' });
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewProjectType({ name: '', description: '', estimatedHours: 0, complexity: 'intermediate' });
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse">Loading project types...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Project Types</h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Project Type
          </button>
        )}
      </div>

      {/* Add new project type form */}
      {isAdding && (
        <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Project Type Name</label>
              <input
                type="text"
                value={newProjectType.name}
                onChange={(e) => setNewProjectType({ ...newProjectType, name: e.target.value })}
                placeholder="e.g., Sour Beer, Fruit Wine, Specialty Brew"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={newProjectType.description}
                onChange={(e) => setNewProjectType({ ...newProjectType, description: e.target.value })}
                placeholder="Brief description of this project type..."
                rows={2}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Estimated Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={newProjectType.estimatedHours}
                  onChange={(e) => setNewProjectType({ ...newProjectType, estimatedHours: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Complexity</label>
                <select
                  value={newProjectType.complexity}
                  onChange={(e) => setNewProjectType({ ...newProjectType, complexity: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                disabled={!newProjectType.name.trim()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </button>
              <button
                onClick={cancelAdd}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing project types list */}
      <div className="space-y-2">
        {projectTypes.length === 0 ? (
          <p className="text-gray-500 text-sm italic">No custom project types yet. Add one above!</p>
        ) : (
          projectTypes.map((projectType) => (
            <div key={projectType.id} className="p-3 bg-white border border-gray-200 rounded-lg">
              {editingId === projectType.id ? (
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      value={editProjectType.name}
                      onChange={(e) => setEditProjectType({ ...editProjectType, name: e.target.value })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <textarea
                      value={editProjectType.description}
                      onChange={(e) => setEditProjectType({ ...editProjectType, description: e.target.value })}
                      rows={2}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={editProjectType.estimatedHours}
                        onChange={(e) => setEditProjectType({ ...editProjectType, estimatedHours: parseFloat(e.target.value) || 0 })}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <select
                        value={editProjectType.complexity}
                        onChange={(e) => setEditProjectType({ ...editProjectType, complexity: e.target.value })}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(projectType.id)}
                      disabled={!editProjectType.name.trim()}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{projectType.name}</h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        projectType.complexity === 'beginner' ? 'bg-green-100 text-green-800' :
                        projectType.complexity === 'advanced' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {projectType.complexity}
                      </span>
                      {(projectType.estimatedHours || 0) > 0 && (
                        <span className="text-xs text-gray-500">
                          {projectType.estimatedHours}h
                        </span>
                      )}
                    </div>
                    {projectType.description && (
                      <p className="text-sm text-gray-600 mt-1">{projectType.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-1 ml-4">
                    <button
                      onClick={() => startEdit(projectType)}
                      className="p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(projectType.id)}
                      className="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};